import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Subject} from 'rxjs';
import {distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {fasciaFornitori, giochiDellaScheda, lobbyCasino, loghiFornitori, testata, tuttiIGiochiDellaScheda} from '../../moduli/leggi';
import {useSkin} from '../../moduli/useSkin';
import {inAnteprima} from '../../skin';
import {eOspite} from '../../utils/currentUser';
import {cambiaPreferito, descriviErrore, giochi as caricaGiochi, preferiti as leggiPreferiti} from '../../services/casinoApi';
import Caricamento from '../common/Caricamento';
import Nota from '../Vetrina/Nota';
import Copertina from './Copertina';
import ScheletroLobby, {RigaSegnaposto} from './Scheletro';
import Pagine, {usePagine, useRicordaScorrimento} from './Pagine';
import FasciaFornitori, {useAlTelefono} from './FasciaFornitori';
import RigaScorrevole from './RigaScorrevole';
import CassettoFornitori, {TastoFornitori} from './CassettoFornitori';
import {logoFornitore} from './fornitoriNoti';
import {moduloDellaLobby, percorsoSezione, sezioneDelCatalogo, stileColori} from './comune';
import './Casino.css';

const TUTTI = '';
const TITOLI = {liveCasino: 'Live casino', virtual: 'Virtual', crashGame: 'Crash game'};
const PREFERITI = '♥';

/**
 * LA LOBBY DEL CASINO' (e del Live casino, stessa pagina con l'altra
 * sezione): la fascia dei fornitori in alto (o le categorie, se il
 * backoffice sceglie cosi'), la ricerca e il filtro per fornitore, poi i
 * giochi a griglia o a righe per categoria.
 *
 * IN CIMA, SU UNA RIGA: a sinistra la ricerca (corta), in mezzo le
 * categorie (o le schede) su una riga che scorre con una freccia d'oro
 * all'inizio e una alla fine (RigaScorrevole), a destra il filtro per
 * fornitore e i Preferiti. Sotto, con la forma «In cima alla lobby» di
 * serie, la FASCIA DEI FORNITORI della pagina aperta, la stessa della Home,
 * che scorre da sola e col mouse; toccando un fornitore si filtra la
 * pagina. Con «Categorie» la fascia non c'e'.
 *
 * I FORNITORI, nella fascia e nel filtro, sono SOLO quelli che hanno giochi
 * nella pagina aperta: nella scheda «Piu' giocati del mese» quelli della
 * scheda, non tutti quelli del catalogo.
 *
 * TUTTO QUELLO CHE SI SCEGLIE STA NELL'INDIRIZZO (?scheda, ?categoria,
 * ?preferiti, ?fornitore, ?cerca, ?pagina): aprendo un gioco e tornando
 * indietro si ritrova la stessa pagina, con lo stesso fornitore, allo
 * stesso punto (useRicordaScorrimento).
 *
 * COSA DECIDE IL BACKOFFICE (moduli Casino' e Live casino): cerca,
 * fornitori, preferiti e prova accesi o spenti; griglia o righe; la misura
 * delle copertine; fondo e testo propri; le SCHEDE (Top 10, Jackpot...), con
 * i giochi scelti a mano. Con le schede le pastiglie in alto e le righe sono
 * le schede (/casino?scheda=top10); senza, le categorie del fornitore.
 * COSA DECIDE IL FORNITORE: quali giochi ci sono, nomi, copertine e
 * categorie. Il sito non ne inventa nessuno: se
 * casino-service non risponde lo dice, con codice e dettaglio, e sotto
 * disegna la lobby a vuoto (Scheletro.js) con la forma scelta dal backoffice.
 *
 * LA GRIGLIA E' LA LISTA INTERA, A PEZZI: quanti giochi si vedono
 * all'apertura lo dice la skin (modulo Giochi per pagina, da 50 a 500, di
 * serie 50), e in fondo «Mostra di piu'» ne aggiunge altrettanti (Pagine.js). Una scheda scelta (?scheda=top10) e' la PAGINA DELLA
 * SCHEDA: tutti i suoi giochi, non solo i primi `quanti`. Le righe che
 * scorrono no: mostrano i primi `quanti` della scheda (al massimo 10), e il
 * nome della riga o «Vedi tutti (n)» aprono la scheda intera.
 *
 * Toccando un gioco si va nella sua pagina; da scollegati si apre la
 * finestra di accesso (il gioco vero si gioca solo col conto). «Prova» apre
 * la pagina in prova gratuita, anche da scollegati.
 */
export default function Lobby({sezione, userService}) {
  useSkin();
  const {t} = useTranslation();
  const navigate = useNavigate();
  const modulo = lobbyCasino(moduloDellaLobby(sezione));
  const percorso = percorsoSezione(sezione);

  const [stato, setStato] = useState({carica: true, errore: '', elenco: []});
  /* La categoria puo' arrivare dall'indirizzo (/casino?categoria=Slot): e' il
     collegamento del menu. Cambia l'indirizzo, cambia la categoria. */
  const [parametri, setParametri] = useSearchParams();
  /* Cambia l'indirizzo: `cambi` e' {parametro: valore}, vuoto = tolto. Filtri
     e pagina sostituiscono la voce della cronologia; aprire una scheda o una
     categoria (`nuovaVoce`) ne aggiunge una, cosi' «indietro» torna alla lobby. */
  const cambiaIndirizzo = (cambi, nuovaVoce) => setParametri((prima) => {
    const p = new URLSearchParams(prima);
    Object.entries(cambi).forEach(([k, v]) => (v ? p.set(k, v) : p.delete(k)));
    return p;
  }, {replace: !nuovaVoce});
  /* Le schede sono della sezione che ha il modulo (Casino', Live casino):
     Virtual e Crash game prendono in prestito il modulo del Casino' per
     cerca, griglia e colori, non le sue schede (i loro giochi sono altri). */
  const schede = moduloDellaLobby(sezione) === sezione ? modulo.schede : [];
  const conSchede = schede.length > 0;
  /* Con le schede la scelta e' una scheda (?scheda=top10), senza una
     categoria del fornitore (?categoria=Slot): la stessa pastiglia, valori
     diversi. Una scheda ha un id: qui la si tiene come «s:<id>». */
  const categoria = parametri.get('preferiti') ? PREFERITI : conSchede
    ? (parametri.get('scheda') ? 's:' + parametri.get('scheda') : TUTTI)
    : (parametri.get('categoria') || TUTTI);
  const setCategoria = (valore) => cambiaIndirizzo({
    preferiti: valore === PREFERITI ? '1' : '',
    scheda: valore.startsWith('s:') ? valore.slice(2) : '',
    categoria: valore !== PREFERITI && !valore.startsWith('s:') ? valore : '',
    pagina: '',
  }, true);
  const schedaScelta = categoria.startsWith('s:') ? schede.find((x) => 's:' + x.id === categoria) || null : null;
  /* Il fornitore puo' arrivare dall'indirizzo (/casino?fornitore=Pragmatic):
     e' il collegamento dei loghi in Home. Vale solo col filtro acceso. */
  const fornitore = modulo.fornitori ? (parametri.get('fornitore') || '') : '';
  /* COL GUSCIO NOVA, SUL TELEFONO, si sceglie prima il fornitore e poi la
     categoria: sotto la ricerca la fascia di TUTTI i fornitori della sezione
     (anche con le categorie in cima nel backoffice), sotto la fascia le
     categorie che hanno giochi del fornitore scelto (Nova.css). Cambiando
     fornitore la categoria riparte da «Tutti» (i Preferiti restano). Dal
     computer e con gli altri gusci resta tutto com'era. */
  const fasciaAlTelefono = testata().stile === 'nova';
  const primaIlFornitore = useAlTelefono(fasciaAlTelefono);
  const setFornitore = (f) => cambiaIndirizzo(primaIlFornitore && !parametri.get('preferiti')
    ? {fornitore: f, scheda: '', categoria: '', pagina: ''} : {fornitore: f, pagina: ''});
  /* Il testo cercato: nella casella subito, nell'indirizzo con lui. */
  const cercaDaIndirizzo = parametri.get('cerca') || '';
  const [cerca, setCercaScritto] = useState(cercaDaIndirizzo);
  useEffect(() => { setCercaScritto(cercaDaIndirizzo); }, [cercaDaIndirizzo]);
  const setCerca = (v) => { setCercaScritto(v); cambiaIndirizzo({cerca: v, pagina: ''}); };
  const [cuori, setCuori] = useState(leggiPreferiti);

  /* CHI GUARDA CAMBIA COSA VEDE. Gli interruttori del casino' valgono per
     nodo della rete, e casino-service decide dal gettone (chi e' dentro vede
     la lobby del suo nodo) o dalla skin (l'ospite). Quindi all'accesso e
     all'uscita l'elenco si rilegge, come la barra in alto rilegge il saldo:
     senza, un giocatore appena entrato terrebbe la lobby dell'ospite finche'
     non cambia pagina, e vedrebbe giochi che per lui sono spenti. */
  const [accesso, setAccesso] = useState(0);
  useEffect(() => {
    if (!userService || typeof userService.isLoggedIn !== 'function') return undefined;
    const stop = new Subject();
    let primo = true;
    userService.isLoggedIn().pipe(distinctUntilChanged(), takeUntil(stop)).subscribe(() => {
      /* Il primo valore arriva subito, all'iscrizione: non e' un cambio, e
         quel caricamento lo fa gia' l'effetto della sezione qui sotto. */
      if (primo) { primo = false; return; }
      setAccesso((n) => n + 1);
    });
    return () => { stop.next(); stop.complete(); };
  }, [userService]);

  useEffect(() => {
    let vivo = true;
    setStato({carica: true, errore: '', elenco: []});
    caricaGiochi(sezioneDelCatalogo(sezione))
        .then((elenco) => vivo && setStato({carica: false, errore: '', elenco}))
        .catch((e) => vivo && setStato({carica: false, errore: descriviErrore(e), elenco: []}));
    return () => { vivo = false; };
  }, [sezione, accesso]);

  const categorie = useMemo(() => {
    const viste = [];
    stato.elenco.forEach((g) => g.categorie.forEach((c) => { if (!viste.includes(c)) viste.push(c); }));
    return viste;
  }, [stato.elenco]);

  /* Il fornitore non da' giochi (errore o elenco vuoto): al loro posto la
     lobby disegnata a vuoto, cosi' la forma del modulo si vede lo stesso. */
  const senzaGiochi = !stato.carica && (!!stato.errore || stato.elenco.length === 0);

  const testo = modulo.cerca ? cerca.trim().toLowerCase() : '';
  const passaFiltri = (g) =>
    (!modulo.fornitori || !fornitore || g.fornitore === fornitore) &&
    (!testo || g.nome.toLowerCase().includes(testo) || g.fornitore.toLowerCase().includes(testo));
  /* Una scheda tiene il SUO ordine (quello scelto nel backoffice), non quello
     del fornitore, e la sua pagina li mostra TUTTI: `quanti` vale per la riga. */
  const dellaPagina = useMemo(() => (schedaScelta ? tuttiIGiochiDellaScheda(schedaScelta, stato.elenco) : stato.elenco).filter((g) =>
    categoria === TUTTI || schedaScelta || (categoria === PREFERITI ? cuori.includes(g.id) : g.categorie.includes(categoria))),
  [stato.elenco, schedaScelta, categoria, cuori]);
  const filtrati = dellaPagina.filter(passaFiltri);
  /* I fornitori della PAGINA APERTA (la scheda, la categoria, i preferiti),
     non di tutto il catalogo: nel filtro e nella fascia ci sono solo quelli
     che qui hanno giochi. Quello scelto resta anche se qui non ne ha,
     cosi' si vede perche' la pagina e' vuota e lo si puo' togliere. */
  const fornitori = useMemo(() => {
    const qui = new Set((primaIlFornitore ? stato.elenco : dellaPagina).map((g) => g.fornitore).filter(Boolean));
    if (fornitore) qui.add(fornitore);
    return Array.from(qui).sort((a, b) => a.localeCompare(b));
  }, [dellaPagina, fornitore, primaIlFornitore, stato.elenco]);
  /* La fascia in cima: i loghi caricati nel backoffice (Moduli › Home ›
     Fornitori) dove il nome combacia, il nome scritto dove non c'e' logo. */
  const loghi = loghiFornitori();
  const vociFascia = fornitori.map((nome) => ({nome, immagine: logoFornitore(nome, loghi)}));
  const conFascia = modulo.inCima === 'fornitori';
  /* Sul telefono la fascia non c'e': il cassetto coi fornitori della pagina
     si apre dal tasto fra i filtri, e si chiude cambiando indirizzo. */
  const [cassetto, setCassetto] = useState(false);
  const chiudiCassetto = React.useCallback(() => setCassetto(false), []);
  useEffect(() => { setCassetto(false); }, [sezione, categoria]);
  /* Le pastiglie: le schede della skin, oppure le categorie del fornitore. */
  const delFornitore = (g) => !primaIlFornitore || !fornitore || g.fornitore === fornitore;
  const pastiglie = conSchede
    ? schede.filter((x) => !primaIlFornitore || !fornitore || tuttiIGiochiDellaScheda(x, stato.elenco).some(delFornitore))
        .map((x) => ({valore: 's:' + x.id, nome: (x.icona ? x.icona + ' ' : '') + x.nome}))
    : categorie.filter((c) => !primaIlFornitore || !fornitore || stato.elenco.some((g) => delFornitore(g) && g.categorie.includes(c)))
        .map((c) => ({valore: c, nome: c}));
  /* Le categorie stanno fra la ricerca e il fornitore, sia con la fascia dei
     fornitori in cima sia senza. */
  const conCategorie = !stato.carica && !stato.errore && pastiglie.length > 0;
  /* La scheda scelta non ha giochi (o nessuno che il fornitore mandi ancora). */
  const schedaVuota = !!schedaScelta && !stato.carica && !stato.errore && stato.elenco.length > 0 &&
    tuttiIGiochiDellaScheda(schedaScelta, stato.elenco).length === 0;

  /* Righe per categoria (o per scheda) solo sulla vista d'insieme: cercando,
     filtrando o scegliendo una pastiglia si guarda una griglia sola. */
  const aRighe = modulo.disposizione === 'righe' && categoria === TUTTI && !testo && !fornitore && pastiglie.length > 0;
  /* La griglia va a pagine (modulo Giochi per pagina): cambiando sezione,
     pastiglia, testo o fornitore si riparte dalla prima. */
  const pagine = usePagine(filtrati, [sezione, categoria, testo, fornitore].join('|'));
  /* Tornando da un gioco, la pagina riparte dal punto in cui la si e' lasciata. */
  useRicordaScorrimento(!stato.carica);

  const rigaCategorie = conCategorie && (
    <RigaScorrevole className={'cas-categorie-riga'} attiva={categoria} etichetta={conSchede ? t('Schede') : t('Categorie')}>
      {[{valore: TUTTI, nome: t('Tutti')}].concat(pastiglie).map((c) => (
        <button type={'button'} key={c.valore || 'tutti'} aria-pressed={categoria === c.valore}
          className={'cas-categoria' + (categoria === c.valore ? ' scelta' : '')} onClick={() => setCategoria(c.valore)}>
          {c.nome}
        </button>
      ))}
    </RigaScorrevole>
  );

  const apri = (g) => {
    if (eOspite()) {
      if (userService && userService.richiediAccesso) userService.richiediAccesso();
      return;
    }
    navigate(`${percorso}/gioco/${encodeURIComponent(g.id)}`);
  };
  const prova = (g) => navigate(`${percorso}/gioco/${encodeURIComponent(g.id)}?prova=1`);
  const copertina = (g) => (
    <Copertina key={g.id} gioco={g} preferito={cuori.includes(g.id)} conPreferiti={modulo.preferiti} conProva={modulo.prova}
      onApri={apri} onProva={prova} onPreferito={(id) => setCuori(cambiaPreferito(id))}/>
  );

  return (
    <div className={'cas-lobby cas-' + modulo.copertine} style={stileColori(modulo.colori)}>
      <div className={'cas-intesta'}>
        <h1>
          {TITOLI[sezione] ? t(TITOLI[sezione]) : t('Casinò')}
          {/* Quanti giochi ha la pagina aperta: lo mostra il guscio Nova, accanto al titolo. */}
          {!stato.carica && !stato.errore && filtrati.length > 0 && (
            <span className={'cas-conta'}>{t('{{count}} giochi', {count: filtrati.length})}</span>
          )}
        </h1>
        {conFascia && categoria !== TUTTI && (
          <p className={'cas-dove'}>
            <button type={'button'} onClick={() => setCategoria(TUTTI)}>{t('Tutti i giochi')}</button>
            <span aria-hidden={'true'}>›</span>
            <strong>
              {categoria === PREFERITI
                ? '♥ ' + t('Preferiti')
                : schedaScelta ? (schedaScelta.icona ? schedaScelta.icona + ' ' : '') + schedaScelta.nome : categoria}
            </strong>
          </p>
        )}
        {(modulo.cerca || modulo.preferiti || conCategorie || (modulo.fornitori && (fornitori.length > 1 || senzaGiochi))) && (
          <div className={'cas-filtri' + (conFascia && modulo.fornitori && vociFascia.length > 1 ? ' con-cassetto' : '') + (conCategorie && !primaIlFornitore ? ' con-categorie' : '')}>
            {modulo.cerca && (
              <input type={'search'} className={'cas-cerca'} value={cerca} onChange={(e) => setCerca(e.target.value)}
                placeholder={t('Cerca gioco o fornitore')} aria-label={t('Cerca gioco o fornitore')}/>
            )}
            {/* Fra la ricerca e il fornitore: le categorie (o le schede) su una riga, con le frecce d'oro. */}
            {conCategorie && !primaIlFornitore && rigaCategorie}
            {modulo.fornitori && (fornitori.length > 1 || senzaGiochi) && (
              <select className={'cas-fornitore'} value={fornitore} onChange={(e) => setFornitore(e.target.value)} aria-label={t('Fornitore')}
                disabled={senzaGiochi}>
                <option value={''}>{t('Tutti i fornitori')}</option>
                {fornitori.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            )}
            {/* Sul telefono, al posto della fascia e della tendina: il tasto che apre il cassetto dei fornitori. */}
            {conFascia && modulo.fornitori && vociFascia.length > 1 && (
              <TastoFornitori quanti={vociFascia.length} scelto={fornitore} aperto={cassetto} onApri={() => setCassetto((a) => !a)}/>
            )}
            {/* I preferiti hanno il loro bottone, in fondo a destra. */}
            {modulo.preferiti && (
              <button type={'button'} className={'cas-preferiti' + (categoria === PREFERITI ? ' scelto' : '')}
                aria-pressed={categoria === PREFERITI} aria-label={t('Preferiti')} onClick={() => setCategoria(categoria === PREFERITI ? TUTTI : PREFERITI)}>
                <span aria-hidden={'true'}>{'♥ '}</span><span className={'cas-preferiti-nome'}>{t('Preferiti')}</span>
              </button>
            )}
          </div>
        )}
      </div>

      {modulo.fornitori && !stato.carica && !stato.errore && vociFascia.length > 1 && (conFascia || fasciaAlTelefono) && (
        <>
          {/* Dal computer la fascia scorre da sola e si ferma sotto il mouse. Sul telefono il guscio Nova
              la mostra sempre, anche con le categorie in cima, e la fa scorrere verso destra; gli altri
              gusci al suo posto hanno il cassetto. */}
          <FasciaFornitori voci={vociFascia} scelto={fornitore} etichetta={t('Fornitori')} marquee {...fasciaFornitori()}
            className={conFascia ? undefined : 'cas-fascia-solo-telefono'} versoDestraAlTelefono={fasciaAlTelefono}
            onScegli={(v) => setFornitore(v.nome === fornitore ? '' : v.nome)}/>
          {conFascia && (
            <CassettoFornitori aperto={cassetto} onChiudi={chiudiCassetto} voci={vociFascia} scelto={fornitore} titolo={t('Fornitori')}
              onScegli={(v) => setFornitore(v.nome)}/>
          )}
        </>
      )}

      {/* Col guscio Nova, sul telefono, le categorie stanno sotto la fascia dei fornitori. */}
      {conCategorie && primaIlFornitore && <div className={'cas-categorie-sotto'}>{rigaCategorie}</div>}

      {stato.carica && <Caricamento forma={'righe'} righe={4} testo={t('Carico i giochi')}/>}
      {stato.errore && (
        <Nota tono={'errore'}>
          {stato.errore}
        </Nota>
      )}
      {!stato.carica && !stato.errore && stato.elenco.length === 0 && (
        <Nota tono={'spento'} giocatore={t('Non ci sono giochi da mostrare per ora.')}>{t('Il fornitore non ha giochi per questa sezione.')}</Nota>
      )}
      {senzaGiochi && inAnteprima() && <ScheletroLobby modulo={modulo}/>}

      {aRighe && pastiglie.map((c) => {
        const scheda = conSchede ? schede.find((x) => 's:' + x.id === c.valore) : null;
        /* La riga di una scheda mostra i primi `quanti`; «Vedi tutti» conta tutti quelli della scheda. */
        const tutti = scheda ? tuttiIGiochiDellaScheda(scheda, filtrati) : filtrati.filter((g) => g.categorie.includes(c.valore));
        const della = scheda ? giochiDellaScheda(scheda, filtrati) : tutti;
        /* Una scheda vuota ha la sua riga, a vuoto e col perche': e' una scelta del backoffice, non del fornitore. */
        if (!della.length && (!scheda || !inAnteprima())) return null;
        /* Il nome della riga fa quello che fa «Vedi tutti»: apre la scheda (o la categoria) intera. */
        const apriTutti = () => setCategoria(c.valore);
        return (
          <section className={'cas-riga'} key={c.valore} aria-label={c.nome}>
            <div className={'cas-riga-testa'}>
              <h2>
                {della.length > 0
                  ? <button type={'button'} className={'cas-riga-nome'} onClick={apriTutti}>{c.nome}</button>
                  : c.nome}
              </h2>
              {della.length > 0 && <button type={'button'} className={'cas-tutti'} onClick={apriTutti}>{t('Vedi tutti')} ({tutti.length})</button>}
            </div>
            {della.length > 0
              ? <div className={'cas-scorre'}>{della.map(copertina)}</div>
              : (
                <>
                  <Nota tono={'spento'}>{t('La scheda «{{nome}}» non ha ancora giochi: si scelgono dal backoffice (Moduli › {{sezione}} › Schede).', {nome: scheda.nome, sezione: sezione === 'liveCasino' ? t('Live casino') : t('Casinò')})}</Nota>
                  <RigaSegnaposto quanti={6} conCuore={modulo.preferiti} conProva={modulo.prova}/>
                </>
              )}
          </section>
        );
      })}

      {schedaVuota && !aRighe && (
        <>
          <Nota tono={'spento'} giocatore={t('Non ci sono ancora giochi in questa pagina.')}>{t('La scheda «{{nome}}» non ha ancora giochi: si scelgono dal backoffice (Moduli › {{sezione}} › Schede).', {nome: schedaScelta.nome, sezione: sezione === 'liveCasino' ? t('Live casino') : t('Casinò')})}</Nota>
          {inAnteprima() && <RigaSegnaposto quanti={6} conCuore={modulo.preferiti} conProva={modulo.prova}/>}
        </>
      )}

      {!aRighe && !schedaVuota && !stato.carica && !stato.errore && stato.elenco.length > 0 && (
        filtrati.length
          ? (
            <>
              <div className={'cas-griglia'}>{pagine.visibili.map(copertina)}</div>
              <Pagine {...pagine}/>
            </>
          )
          : (
            <Nota>
              {categoria === PREFERITI
                ? t('Nessun preferito: tocca il cuore su un gioco per ritrovarlo qui.')
                : t('Nessun gioco trovato.')}
            </Nota>
          )
      )}
    </div>
  );
}
